import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, Typography, TextField, Grid, Switch, FormControlLabel } from "@mui/material";
import { useAuth, useLoading, useNavigation } from "../../Context/Contexts";
import { ListQuery, PickPackLineViewModel } from "../../Types/ExportTypes";
import { useTranslation } from "react-i18next";
import ImageBox from "../../components/Image/ImageBox";
import LocalizedNumberInput from "../../components/Input/LocalizedNumberInputs";
import OrderInfoHeader from "../../components/Warehouse/OrderInfoHeader";
import CenteredCard from "../../components/Card/CenteredCardLayout";
import ResponsivePagination from "../../components/ResponsivePagination";
const PackOrderPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
 const { orderNo = "" } = params || {};
  const { t } = useTranslation();
    const { authService } = useAuth();
    const { showLoading, hideLoading, isLoading } = useLoading();
  const { navigate } = useNavigation();
  const [query, setQuery] = useState<ListQuery>({ page: 0, pageSize: 1 });
  const [lines, setLines] = useState<PickPackLineViewModel[]>([]);
  const [total, setTotal] = useState(0);
  const [quantity, setQuantity] = useState<number>(0);
  const [content, setContent] = useState("");
  const [packageCount, setPackageCount] = useState<number>(1);
  const [showInfo, setShowInfo] = useState(false);
  const [nextOrder, setNextOrder] = useState<string | null>(null);

  const fetchLines = async () => {
    showLoading();
    try {
      const res = await authService.post<{ items: PickPackLineViewModel[]; count: number }>(`/pickpackApi/GetPackLines?orderNo=${orderNo}`, query);
      setLines(res.items);
      setTotal(res.count);
      return res;
    } finally {
      hideLoading();
    }
  };

  useEffect(() => { fetchLines(); }, [query, orderNo]);

  const next = () => setQuery({ ...query, page: query.page + 1 });
  const prev = () => setQuery({ ...query, page: Math.max(query.page - 1, 0) });

  const packLine = async () => {
    if (!line) return;
    showLoading();
    try {
      await authService.post("/pickpackApi/SetPackedQuantity", { lineId: line.id, quantity });
      const result = await fetchLines();
      if (result && result.count === 0) {
        await authService.post("/pickpackApi/DispatchOrder?orderNo=" + orderNo, {});
        await loadNextOrder();
      } else {
        next();
      }
    } finally {
      hideLoading();
    }
  };

  const dispatchOrder = async () => {
    showLoading();
    try {
      await authService.post(`/pickpackApi/DispatchOrder?orderNo=${orderNo}`, {});
      await loadNextOrder();
    } finally {
      hideLoading();
    }
  };

  const updateContent = async () => {
    await authService.post("/pickpackApi/SetWaybillContent", { documentNo: orderNo, content });
  };

  const updatePackageCount = async () => {
    await authService.post("/pickpackApi/SetPackageCount", { documentNo: orderNo, packageCount });
  };

  const loadNextOrder = async () => {
    try {
      const info = await authService.get<any>(`/orderApi/GetOrder/?documentNo=${orderNo}`);
      const batchId = info.orderBatchId;
      if (!batchId) return;
      const res = await authService.get<{ orderNo: string | null }>(`/batchApi/NextOrder?batchId=${batchId}&orderNo=${orderNo}`);
      setNextOrder(res.orderNo ?? null);
    } catch {
      setNextOrder(null);
    }
  };

  const line = lines[0];
  useEffect(() => {
    setQuantity(line?.quantity ?? 0);
  }, [line]);
    return (
        <CenteredCard title={t("common.pack")} isLoading={isLoading}>
    <Box>
      <FormControlLabel control={<Switch checked={showInfo} onChange={(e) => setShowInfo(e.target.checked)} />} label={t("common.showOrderInfo")} />
      {showInfo && (
        <OrderInfoHeader orderNo={orderNo} showShipping />
      )}
      <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button variant="contained" color="success" onClick={dispatchOrder}>{t("common.dispatch")}</Button>
        <TextField fullWidth label="Content" size="small" value={content} onChange={(e) => setContent(e.target.value)} onBlur={updateContent} />
        <LocalizedNumberInput label="Package count" value={packageCount} onValueChange={(v) => setPackageCount(v ?? 1)} size="small" onBlur={updatePackageCount} />
        {nextOrder && (
          <Button variant="contained" onClick={() => navigate({ path: "warehouse/pack/edit", params: { orderNo: nextOrder } })}>{t("common.openNextOrder")}</Button>
        )}
      </Box>
      {line && (
        <Card>
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={12} sx={{ textAlign: "center" }}>
                <ImageBox imageUrl={line.img_url ?? ""} altText={line.description ?? ""} fallbackText={t("common.noImage")}/>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: "bold" }}>{line.itemNo}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography sx={{ color: "gray" }}>{line.description}</Typography>
              </Grid>
              {line.barcode && (
                <Grid item xs={12}>
                  <Typography variant="body2" sx={{ color: "gray" }}>{t("common.barcode")}: {line.barcode}</Typography>
                </Grid>
              )}
              <Grid item xs={12}>
                <Typography>{t("common.quantity")}: {line.quantity}</Typography>
                <Typography>{t("common.packed")}: {line.packedQty}</Typography>
              </Grid>
              <Grid item xs={12}>
                <LocalizedNumberInput label={t("common.qtyToPack")} value={quantity} onValueChange={(v) => setQuantity(v ?? 0)} size="small" />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      {line && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Button variant="contained" color="success" onClick={packLine}>{t("common.pack")}</Button>
        </Box>
      )}
      <ResponsivePagination
        page={query.page}
        pageSize={query.pageSize}
        totalCount={total}
        onPageChange={(newPage) => setQuery((prev) => ({ ...prev, page: newPage }))}
        sx={{ marginTop: "1rem" }}
      />
            </Box>
    </CenteredCard>
  );
};

export default PackOrderPage;